/**
 * AI 智能分类 · SSE 客户端
 *
 * 协议参考 ai-folder-classifier 接口文档（钉钉 QG53mjyd80R04wE4HgYEnN3RV6zbX04v）§2：
 *
 *   POST /web/workgroup/folder/ai/chat/send
 *   body: { conversation_id, message, group_id }
 *   response: text/event-stream
 *      event: progress\ndata: {"kind":"round","round":0}\n\n
 *      event: progress\ndata: {"kind":"llm_delta","delta":"..."}\n\n
 *      event: progress\ndata: {"kind":"tool_start","name":"...","args":"..."}\n\n
 *      event: progress\ndata: {"kind":"tool_end","name":"...","ok":true,"result":"..."}\n\n
 *      event: progress\ndata: {"kind":"final","content":"...","classify_state":"...","final":{"type":"final_proposal","raw":{...}}}\n\n
 *      event: progress\ndata: {"kind":"heartbeat"}\n\n
 *      event: done\ndata: {"conversation_id":"...","message_id":...,"rounds":N,"final":{...}}\n\n
 *      event: error\ndata: {"error":"..."}\n\n
 *
 * 本文件抽象出 `streamChat()` 异步函数，调用方传入 onDelta / onDone / onError 回调。
 *
 * 注意：使用 fetch + ReadableStream getReader() 而不是浏览器 EventSource API，
 * 因为 EventSource 不支持 POST 请求。
 */

import type {
  AIClassifyStreamEvent,
  AssistantAction,
  AssistantCard,
  ChatSendRequest,
  PlanFolder,
  ToolCallRecord
} from '../../../types/ai-classify'
import {
  displayToolName,
  summarizeArgs,
  summarizeResult
} from './toolDisplayMap'

/** 流回调集合 */
export interface StreamHandlers {
  /** 累计 delta 内容；可被多次调用 */
  onDelta: (content: string) => void
  /** 一次性的 done 事件（流末尾） */
  onDone: (event: Extract<AIClassifyStreamEvent, { type: 'done' }>) => void
  /** error 事件（无论流内 SSE error 还是网络/解析错误，统一走这里） */
  onError: (error: string) => void
  /**
   * 工具调用 start · streaming 期间用于实时展示思考过程（v4.4 新增）
   *
   * SSE 协议：event:progress data:{kind:tool_start, tool_call_id, name, args}
   * record 已通过 toolDisplayMap 翻译成业务语言（toolName 保留原值，
   * paramsLabel 是浓缩摘要）
   */
  onToolStart?: (record: ToolCallRecord) => void
  /**
   * 工具调用 end · 配对 onToolStart（按 id 匹配）
   *
   * SSE 协议：event:progress data:{kind:tool_end, tool_call_id, ok, result}
   */
  onToolEnd?: (record: ToolCallRecord) => void
  /**
   * progress kind=final 提前到达回调 · 让前端在 done 之前就能渲染 final_proposal
   *
   * 后端在 LLM 输出收敛后会立刻 emit 一条 progress kind=final（含完整 final.raw），
   * 紧接着才发 event:done。两者间隔通常几十~几百毫秒，但用户感知"等很久才看到
   * 文件夹列表"——因为前端原本只在 done 事件触发渲染。
   *
   * 本回调让 UI 在 progress kind=final 那一刻就把 card 渲染出来（status 仍为
   * streaming），等 done 事件再切到 done 状态做最终收尾（toolCalls 折叠等）。
   */
  onFinalPartial?: (event: {
    action: AssistantAction
    card: AssistantCard | undefined
    classifyState?: string
    /** 后端 session.status (awaiting_choice 时前端隐藏 FolderPlanPreview, 截图 15/16 修复) */
    sessionId?: string
    sessionStatus?: 'active' | 'awaiting_choice'
  }) => void
  /**
   * 维度链更新 · 后端 ApplyDimensionOp 成功落库一个 dimension_op 后推（维度链架构 v6）。
   *
   * SSE: event:progress data:{kind:dimension_updated, session_id, chain_version,
   *      expanded_size, layer_count, op, affected_layer_id, layers_summary}
   *
   * 前端据此：① 刷新「当前维度链」展示 ② 用 chain_version 作 ETag 重新拉
   * /session/preview 渲染文件夹预览树。
   */
  onDimensionUpdated?: (event: {
    sessionId: string
    chainVersion: number
    expandedSize: number
    layerCount: number
    op: string
    affectedLayerId: number
    layersSummary: unknown[]
  }) => void
}

/** 选项 */
export interface StreamChatOptions {
  /** 真实端点路径 · 默认 `/web/workgroup/folder/ai/chat/send`（webxcameraapi BFF 路由） */
  endpoint?: string
  /** AbortSignal · 用于取消流（如组件卸载） */
  signal?: AbortSignal
}

// ⚠️ 必须拼 __API_SERVER_HOST__（webpack DefinePlugin 注入）—— 与 actions/folder.js
// 和 actions/aiChat.js 一致。不拼会让 fetch 走相对路径打到 SPA 域（pic.xhey.top
// / pictest.xhey.top）→ 静态资源域不接受 POST/SSE → 返 405。
//
// 用函数惰性读取该全局，而非模块顶层 const：顶层读取会让 Jest 一 import 本模块就
// ReferenceError（DefinePlugin 全局测试环境没有），惰性化后 SSE 解析逻辑可被单测。
declare const __API_SERVER_HOST__: string
function defaultEndpoint(): string {
  return __API_SERVER_HOST__ + '/web/workgroup/folder/ai/chat/send'
}

/**
 * 发起一轮聊天 · 解析 SSE 流并回调
 *
 * @returns Promise · 在 done / error 事件后 resolve；signal abort 时 reject
 */
export async function streamChat(
  request: ChatSendRequest,
  handlers: StreamHandlers,
  options: StreamChatOptions = {}
): Promise<void> {
  const { endpoint = defaultEndpoint(), signal } = options

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream'
    },
    body: JSON.stringify(request),
    signal,
    credentials: 'include'
  })
  if (!res.ok) {
    handlers.onError(`HTTP ${res.status} ${res.statusText}`)
    return
  }
  if (!res.body) {
    handlers.onError('响应体为空')
    return
  }

  await readSseStream(res.body, handlers)
}

// ============================================================
// SSE 行解析 · 与 demo web/index.html 同款做法
// ============================================================

async function readSseStream(
  body: ReadableStream<Uint8Array>,
  handlers: StreamHandlers
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let currentEvent = ''

  // 累计跨事件的状态（progress kind=final 先到，done 后到，需在 done 时合并）
  const state: SseAccumState = {
    classifyState: undefined,
    finalType: undefined,
    finalRaw: undefined,
    toolStartedAt: new Map(),
    sseEventLog: []
  }

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, '')
        if (!line) {
          continue
        }
        if (line.startsWith('event: ')) {
          currentEvent = line.slice('event: '.length).trim()
          continue
        }
        if (line.startsWith('data: ')) {
          const dataStr = line.slice('data: '.length)
          dispatchSseLine(currentEvent, dataStr, handlers, state)
          continue
        }
      }
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return
    handlers.onError((err as Error).message || 'SSE 读取异常')
  }
}

interface SseAccumState {
  classifyState?: unknown
  finalType?: AssistantAction
  finalRaw?: unknown
  /**
   * 工具调用 start 时间戳缓存 · key=tool_call_id
   * tool_end 时拿差值算 durationMs，避免后端没给耗时
   */
  toolStartedAt?: Map<string, number>
  /**
   * 本次 send 收到的完整 SSE 事件流 (按到达顺序) · 仅 done 时调试打印用。
   * llm_delta 高频流会被合并为单条 (text=拼接全文, payloads=逐帧明细)。
   */
  sseEventLog: SseLogEntry[]
}

type SseLogEntry =
  | {
      event: 'progress'
      kind: 'llm_delta'
      deltaCount: number
      /** 所有 llm_delta.delta 拼接全文 */
      text: string
      /** 逐帧原始 payload, 便于细看其他字段 */
      payloads: any[]
    }
  | {
      event: string
      kind?: string
      payload: any
    }

/**
 * 适配 ai-folder-classifier SSE 协议（接口文档 §2 SSE 事件类型）：
 *
 *   event: progress  data: {kind: "round" | "llm_delta" | "tool_start" | "tool_end" | "final" | "heartbeat", ...}
 *   event: done      data: {conversation_id, message_id, rounds, final?:{type, raw}}
 *   event: error     data: {error: "..."}
 *
 * 关键映射：
 *   progress kind=llm_delta → handlers.onDelta(payload.delta)
 *   progress kind=final     → 缓存 classify_state / final.type / final.raw（done 时合并）
 *   progress kind=others    → 忽略（round/tool_start/tool_end/heartbeat 暂不渲染，留作 v2）
 *   done                    → handlers.onDone（card 由后端 final.raw 转换成前端类型）
 *   error                   → handlers.onError
 */
// export 仅为单测可直接喂单条 SSE 事件（test/aiClassifySse.test.js）；运行时仍由
// readSseStream 内部调用。
export function dispatchSseLine(
  event: string,
  data: string,
  handlers: StreamHandlers,
  state: SseAccumState
): void {
  let payload: any
  try {
    payload = JSON.parse(data)
  } catch {
    handlers.onError(`SSE data JSON 解析失败: ${data}`)
    return
  }
  // 先按到达顺序记入事件流, done 时统一打印 (见 handleDoneEvent)
  appendSseLogEntry(state, event, payload)
  switch (event) {
    case 'progress':
      handleProgressEvent(payload, handlers, state)
      break
    case 'done':
      handleDoneEvent(payload, handlers, state)
      break
    case 'error':
      handlers.onError(payload?.error || '未知错误')
      break
    default:
      // 未知 event 名忽略（兼容性）
      break
  }
}

/**
 * 把单条 SSE 事件记入 state.sseEventLog (按到达顺序)。
 * 对 llm_delta 高频流做"连续合并": 与上一条同为 llm_delta 时拼到同一条目,
 * text 持续累加, payloads 留下逐帧明细。其他事件每条独立入栈。
 */
function appendSseLogEntry(
  state: SseAccumState,
  event: string,
  payload: any
): void {
  if (event === 'progress' && payload?.kind === 'llm_delta') {
    const last = state.sseEventLog[state.sseEventLog.length - 1]
    if (
      last &&
      last.event === 'progress' &&
      (last as any).kind === 'llm_delta'
    ) {
      const merged = last as Extract<SseLogEntry, { kind: 'llm_delta' }>
      merged.deltaCount += 1
      if (typeof payload.delta === 'string') merged.text += payload.delta
      merged.payloads.push(payload)
      return
    }
    state.sseEventLog.push({
      event: 'progress',
      kind: 'llm_delta',
      deltaCount: 1,
      text: typeof payload.delta === 'string' ? payload.delta : '',
      payloads: [payload]
    })
    return
  }
  state.sseEventLog.push({
    event,
    kind: typeof payload?.kind === 'string' ? payload.kind : undefined,
    payload
  })
}

function handleProgressEvent(
  payload: any,
  handlers: StreamHandlers,
  state: SseAccumState
): void {
  switch (payload?.kind) {
    case 'llm_delta':
      if (typeof payload.delta === 'string') {
        handlers.onDelta(payload.delta)
      }
      break
    case 'final':
      // 缓存到 state；done 事件到来时合并出最终 onDone payload
      state.classifyState = payload.classify_state
      if (payload.final && typeof payload.final === 'object') {
        state.finalType = payload.final.type as AssistantAction
        state.finalRaw = payload.final.raw
      }
      // 立即把 card 推给 UI（不等 done），让 folder 列表尽早显示
      if (handlers.onFinalPartial && state.finalType) {
        // 后端在 ask/delete_confirm 卡时透传 session_status='awaiting_choice'
        // 让前端 selectionSlice 切 chainStatus → FolderPlanPreview 自动隐藏
        const ss =
          typeof payload.session_status === 'string'
            ? (payload.session_status as 'active' | 'awaiting_choice')
            : undefined
        const sid =
          typeof payload.session_id === 'string'
            ? payload.session_id
            : undefined
        handlers.onFinalPartial({
          action: state.finalType,
          card: transformBackendCard(state.finalType, state.finalRaw),
          classifyState:
            typeof state.classifyState === 'string'
              ? state.classifyState
              : undefined,
          sessionId: sid,
          sessionStatus: ss
        })
      }
      break
    case 'tool_start':
      handleToolStart(payload, handlers, state)
      break
    case 'tool_end':
      handleToolEnd(payload, handlers, state)
      break
    case 'dimension_updated':
      // 维度链架构 v6 · 后端 ApplyDimensionOp 成功后推。
      // payload: {session_id, chain_version, expanded_size, layer_count, op,
      //           affected_layer_id, layers_summary}
      if (handlers.onDimensionUpdated) {
        handlers.onDimensionUpdated({
          sessionId: String(payload.session_id ?? ''),
          chainVersion:
            typeof payload.chain_version === 'number'
              ? payload.chain_version
              : 0,
          expandedSize:
            typeof payload.expanded_size === 'number'
              ? payload.expanded_size
              : 0,
          layerCount:
            typeof payload.layer_count === 'number' ? payload.layer_count : 0,
          op: String(payload.op ?? ''),
          affectedLayerId:
            typeof payload.affected_layer_id === 'number'
              ? payload.affected_layer_id
              : 0,
          layersSummary: Array.isArray(payload.layers_summary)
            ? payload.layers_summary
            : []
        })
      }
      break
    case 'round':
    case 'heartbeat':
    default:
      // round / heartbeat 暂不渲染（v2 可加 round 计数）
      break
  }
}

/**
 * tool_start: {kind, tool_call_id, name, args}
 *  · args 可能是 JSON string 或 object，summarizeArgs 兼容两种
 *  · onToolStart 推一条占位 ToolCallRecord（无 resultSummary / durationMs），
 *    streaming UI 显示 "执行中…" 状态
 */
function handleToolStart(
  payload: any,
  handlers: StreamHandlers,
  state: SseAccumState
): void {
  if (!handlers.onToolStart) return
  const id = String(payload.tool_call_id ?? '')
  const toolName = String(payload.name ?? '')
  if (!id || !toolName) return
  const startedAtMs = Date.now()
  if (state.toolStartedAt) state.toolStartedAt.set(id, startedAtMs)
  handlers.onToolStart({
    id,
    toolName,
    paramsLabel: summarizeArgs(payload.args),
    startedAtMs // 2026-05-15 时间轴: 让 ToolCallsSummary 推断"思考期"用
  })
  // 后端将来可在 tool_start 给 display_name 直接覆盖前端展示
  // 当前优先级：后端 display_name > 前端 displayToolName(toolName)
  void displayToolName // 引用一次防止 tree-shake 变量未用警告（实际由 UI 调）
}

/**
 * tool_end: {kind, tool_call_id, name, ok, result}
 *  · result 通常是 JSON string，summarizeResult 提炼数量类字段
 *  · durationMs 由 state.toolStartedAt 差值算（后端没给耗时）
 */
function handleToolEnd(
  payload: any,
  handlers: StreamHandlers,
  state: SseAccumState
): void {
  if (!handlers.onToolEnd) return
  const id = String(payload.tool_call_id ?? '')
  const toolName = String(payload.name ?? '')
  if (!id) return
  const ok = payload.ok !== false
  const startedAt = state.toolStartedAt?.get(id)
  const endedAtMs = Date.now()
  const durationMs =
    typeof startedAt === 'number' ? endedAtMs - startedAt : undefined
  if (state.toolStartedAt) state.toolStartedAt.delete(id)
  handlers.onToolEnd({
    id,
    toolName,
    resultSummary: summarizeResult(payload.result, ok),
    durationMs,
    endedAtMs, // 2026-05-15 时间轴: 思考期推断用
    ok
  })
}

function handleDoneEvent(
  payload: any,
  handlers: StreamHandlers,
  state: SseAccumState
): void {
  // 调试: done 时打印本次 send 的完整 SSE 事件流 (按到达顺序)
  // llm_delta 高频流已合并为单条 (text=拼接全文, payloads=逐帧明细),
  // 其他事件 (final / dimension_updated / tool_start / tool_end / done / error ...) 每条独立。
  // eslint-disable-next-line no-console
  console.log('[ai/chat/send done] SSE 事件流', {
    conversationId: payload?.conversation_id,
    messageId: payload?.message_id,
    eventCount: state.sseEventLog.length,
    events: state.sseEventLog
  })
  // done 事件本身可能带 final（与 progress.final 是同一份），优先 done 自带的
  const finalType = (payload?.final?.type as AssistantAction) ?? state.finalType
  const finalRaw = payload?.final?.raw ?? state.finalRaw
  const card = transformBackendCard(finalType, finalRaw)
  // 后端 done 事件携带 message_id（ai_chat_message.id）· 反馈接口入参
  // 协议：event: done data: {conversation_id, message_id, rounds, final?}
  const rawMid = payload?.message_id
  const backendMessageId =
    typeof rawMid === 'number'
      ? rawMid
      : typeof rawMid === 'string' && rawMid !== ''
        ? Number(rawMid)
        : undefined
  // 后端在本次 send 创建 / 复用的会话 id · 用户在新对话欢迎页发首条消息时
  // (请求 body conversation_id 传空) 后端落库新会话并把 id 放在这里返回。
  const rawCid = payload?.conversation_id
  const conversationId =
    typeof rawCid === 'string' && rawCid !== '' ? rawCid : undefined
  handlers.onDone({
    type: 'done',
    // 后端用 assistant_text 透传给用户看的「人话」回复。response_type=text 的纯文本
    // 回合（如发「确认」后的引导「请点击下方按钮…」）只有这一个字段承载内容、不带 card，
    // 漏读会让该轮 assistant 气泡为空被空壳守卫隐藏（界面无反应）。缺失/非串时回落空串。
    message:
      typeof payload?.assistant_text === 'string' ? payload.assistant_text : '',
    action: finalType ?? ('final_proposal' as AssistantAction),
    card,
    classifyState: state.classifyState,
    backendMessageId:
      backendMessageId != null && Number.isFinite(backendMessageId)
        ? backendMessageId
        : undefined,
    conversationId
  })
}

/** 把 ai-folder-classifier 的 final.raw 转成前端 AssistantCard 类型 */
function transformBackendCard(
  type: AssistantAction | undefined,
  raw: any
): AssistantCard | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  switch (type) {
    case 'next_level':
      return {
        kind: 'next_level',
        question: raw.question ?? '',
        options: (raw.options ?? []) as any[]
      } as AssistantCard
    case 'ask':
      // 2026-05-18 截图 32 修复: 透传 picker 字段 (自由选择多选 UI).
      // 后端 raw.picker = {kind:"layer_values_multiselect", target_layer_id, max_choice}.
      // ChoiceOptionsCard 检测 picker.kind 路由到 LayerValuesMultiselectPicker
      // 拉 layer values 渲染 checkbox. 漏传 → AskCard.picker undefined → 只显示问句没选项.
      return {
        kind: 'ask',
        question: raw.question ?? '',
        options: (raw.options ?? []) as any[],
        picker: raw.picker
      } as AssistantCard
    case 'delete_confirm':
      return {
        kind: 'delete_confirm',
        summary: typeof raw.summary === 'string' ? raw.summary : undefined,
        folders: Array.isArray(raw.folders)
          ? (raw.folders as any[]).map((f) => ({
              folder_id: Number(f?.folder_id ?? f?.folderID ?? 0) || 0,
              folder_name: String(f?.folder_name ?? f?.folderName ?? ''),
              photo_count:
                typeof f?.photo_count === 'number'
                  ? f.photo_count
                  : typeof f?.photoCount === 'number'
                    ? f.photoCount
                    : undefined,
              has_children: !!(f?.has_children ?? f?.hasChildren)
            }))
          : []
      } as AssistantCard
    default:
      return undefined
  }
}

/**
 * 把后端给的扁平 folders 拆成多级树。
 *
 * 兼容两种父子表达协议（按优先级）：
 *   协议 A · @ref 链（2026-05 SOTA, photoBookMcp batch_create_with_rule schema）
 *     {book_name:"销售部", parent_book_id:0,        rule:{...}}
 *     {book_name:"张三",   parent_book_id:"@销售部", rule:{...}}
 *     {book_name:"李四",   parent_book_id:"@销售部", rule:{...}}
 *   协议 B · "/" 分段（旧 v3, 仅 book_name 表达层级, 无 parent_book_id）
 *     {book_name:"销售部/张三", rule:{...}}
 *
 * 单条 raw 同时含 "/" 和 "@ref" 时，优先按 @ref 走（A 协议）。
 *
 * 规则：
 *   · 中间层文件夹（容器）：rule 留空（photobook 接受空 rule）
 *   · 叶子文件夹：继承后端给的 rule / samples / estimatedCount
 *   · 同前缀只建一次（Map 共享），"A/B/X" + "A/B/Y" 共享 "A" 和 "A/B"
 *   · @ref 找不到父（父 raw 在数组里更靠后 / 拼写不一致）→ 当根节点挂顶层，
 *     等更后面的轮次拿到完整 raw 时父出现，子还是挂到正确位置
 *     （为此本函数先一次性 pass 1 建所有顶层 + 二次 pass 2 解析 @ref）
 */
export function buildFolderForest(rawFolders: any[]): PlanFolder[] {
  interface Node {
    folder: PlanFolder
    children: Map<string, Node>
  }
  const root = new Map<string, Node>()

  function makeId(): string {
    return `srv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  }

  function emptyRule(): PlanFolder['rule'] {
    return {
      keywords: [],
      keywordAndOr: 0,
      times: [],
      departmentUserID: [],
      departmentID: [],
      nicknames: []
    }
  }

  /**
   * normalizeRule · 把后端 raw.rule（可能字段不全 / 类型不对）规整成严格的
   * PlanFolder['rule'] 形态。每个 array 字段都走 Array.isArray 兜底成 []，
   * 避免下游 rule.times.length 之类访问 NPE。
   *
   * Bad case 2026-05-07 · 用户建空夹"北京外星人"，LLM 输出 rule 只填
   * { keywords:["北京外星人"], keywordAndOr:0 }（缺 times / departmentUserID 等）。
   * 旧实现 `(raw?.rule as PlanFolder['rule']) ?? emptyRule()` 走 raw cast,
   * 这些缺失字段保持 undefined → 下游 FolderPlanPreview 等组件访问
   * `f.rule.times.length` 直接 NPE → 用户看到红框
   * "Cannot read properties of undefined (reading 'length')"。
   */
  function normalizeRule(raw: unknown): PlanFolder['rule'] {
    const r = (raw ?? {}) as Record<string, unknown>
    return {
      keywords: Array.isArray(r.keywords)
        ? (r.keywords as unknown[]).map(String)
        : [],
      keywordAndOr: r.keywordAndOr === 1 ? 1 : 0,
      times: Array.isArray(r.times)
        ? (r.times as Array<Record<string, unknown>>).map((t) => ({
            start: String(t?.start ?? ''),
            end: String(t?.end ?? '')
          }))
        : [],
      departmentUserID: Array.isArray(r.departmentUserID)
        ? (r.departmentUserID as unknown[]).map(String)
        : [],
      departmentID: Array.isArray(r.departmentID)
        ? (r.departmentID as unknown[]).map(String)
        : [],
      nicknames: Array.isArray(r.nicknames)
        ? (r.nicknames as unknown[]).map(String)
        : []
    }
  }

  function ensureNode(
    map: Map<string, Node>,
    parentId: 0 | string,
    segment: string
  ): Node {
    const existing = map.get(segment)
    if (existing) return existing
    const node: Node = {
      folder: {
        id: makeId(),
        folderName: segment,
        parentFolderID: parentId,
        source: 8, // BookAgentSourceType
        rule: emptyRule(),
        estimatedCount: 0,
        samples: [],
        children: []
      },
      children: new Map()
    }
    map.set(segment, node)
    return node
  }

  // ── 协议 A · @ref 链 ──
  // 先扫一遍把所有顶层 (parent_book_id=0) 节点建好, 第二遍再挂 @ref 子级.
  // 这样即便 raw 数组顺序错乱 (子先 父后) 也能正确建树。
  // 关键判别: parent_book_id 类型决定走 A 协议还是 B 协议:
  //   · 字符串 "@xxx" → A 协议 (worker 解析 @ref)
  //   · 数字 0 / 缺省 → 根节点 (可能 A 协议根, 也可能 B 协议)
  // 单条 raw 的 book_name 同时含 "/" → 即使 parent_book_id="@xxx" 也按 / 拆段
  // (兼容老 mock; 实际新协议下 LLM 不会两种混用)。

  // root by book_name —— @ref 走的查找表
  const refRoot = new Map<string, Node>()
  const hasAtRef = (raw: any): boolean =>
    typeof raw?.parent_book_id === 'string' &&
    (raw.parent_book_id as string).startsWith('@')

  // Pass 1: 把所有 parent_book_id=0 / 缺省的当顶层处理 (走 A 协议且单段 book_name)
  // —— 这一遍只处理"非分段 / 顶层" raw, 给 Pass 2 准备父节点查找表。
  // 注意: 同时也处理 B 协议的 / 分段 (走老路径)
  for (const raw of rawFolders) {
    const fullName = String(raw?.book_name ?? raw?.name ?? '').trim()
    if (!fullName) continue

    // 含 "/" → 走老 B 协议拆段
    if (fullName.includes('/') && !hasAtRef(raw)) {
      const segments = fullName
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
      if (segments.length === 0) continue
      let cursor = root
      let parentId: 0 | string = 0
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]
        const isLeaf = i === segments.length - 1
        const node = ensureNode(cursor, parentId, seg)
        if (isLeaf) {
          node.folder.rule = normalizeRule(raw?.rule)
          node.folder.estimatedCount = Number(raw?.estimated_count ?? 0) || 0
          node.folder.samples = Array.isArray(raw?.samples)
            ? (raw.samples as any[]).map((s) => String(s))
            : []
        }
        parentId = node.folder.id
        cursor = node.children
      }
      // 顶层节点也同步登记 refRoot, 防止后面 @ref 子级找不到 (协议混用兜底)
      const topSeg = segments[0]
      const topNode = root.get(topSeg)
      if (topNode) refRoot.set(topSeg, topNode)
      continue
    }

    // 不含 "/" 的根级 raw (A 协议顶层): 直接挂 root + refRoot
    if (!hasAtRef(raw)) {
      const node = ensureNode(root, 0, fullName)
      node.folder.rule = normalizeRule(raw?.rule)
      node.folder.estimatedCount = Number(raw?.estimated_count ?? 0) || 0
      node.folder.samples = Array.isArray(raw?.samples)
        ? (raw.samples as any[]).map((s) => String(s))
        : []
      refRoot.set(fullName, node)
    }
  }

  // Pass 2: 解析 A 协议 @ref 子级, 挂到对应父节点的 children 下。
  // 父找不到 (LLM 没先吐父 / 拼写不一致) → 当根节点降级处理, 用户至少看得见。
  for (const raw of rawFolders) {
    if (!hasAtRef(raw)) continue
    const fullName = String(raw?.book_name ?? raw?.name ?? '').trim()
    if (!fullName) continue
    const parentName = String(raw.parent_book_id as string)
      .slice(1)
      .trim()
    const parentNode = refRoot.get(parentName)
    if (parentNode) {
      const node = ensureNode(
        parentNode.children,
        parentNode.folder.id,
        fullName
      )
      node.folder.rule = normalizeRule(raw?.rule)
      node.folder.estimatedCount = Number(raw?.estimated_count ?? 0) || 0
      node.folder.samples = Array.isArray(raw?.samples)
        ? (raw.samples as any[]).map((s) => String(s))
        : []
      // 子节点也登记一份 refRoot, 支持三级及更深 @ref 嵌套
      refRoot.set(fullName, node)
    } else {
      // 父找不到 → 当根挂顶层, 让用户至少看见 (而不是丢失)
      const node = ensureNode(root, 0, fullName)
      node.folder.rule = normalizeRule(raw?.rule)
      node.folder.estimatedCount = Number(raw?.estimated_count ?? 0) || 0
      node.folder.samples = Array.isArray(raw?.samples)
        ? (raw.samples as any[]).map((s) => String(s))
        : []
      refRoot.set(fullName, node)
    }
  }

  function flatten(map: Map<string, Node>): PlanFolder[] {
    return Array.from(map.values()).map((n) => ({
      ...n.folder,
      children: flatten(n.children)
    }))
  }
  return flatten(root)
}
